import { mapGetters, mapMutations } from 'vuex'
import pluralize from 'pluralize'
import Moment from 'moment'
import numeral from 'numeral'
import { extendMoment } from 'moment-range'
import percentRank from 'percentile-rank'
import camelCase from 'camelcase'

const moment = extendMoment(Moment)

export const Helpers = {
  computed: {
    ...mapGetters([
      'user',
      'isSuper',
      'isStaff',
      'isBusy',
      'isDark',
      'online',
      'socket',
      'snackbar',
      'paneSize'
    ]),
    /**
     * Returns application version
     * @returns {String}
     */
    server() {
      let host = window.location.hostname
      let port = host == 'localhost' ? 8000 : null
      let protocol = window.location.protocol

      let SERVER = port == null ? `${protocol}//${host}` : `${protocol}//${host}:${port}`
      return SERVER
    },
  },
  methods: {
    ...mapMutations([
      'setBusy',
      'closeSnackbar',
      'setPaneSize',
    ]),
    /**
     * Ensure WebSocket connection is active
     */
    ensureWebSocketConnection() {
      if (this.user && !this.online) {
        this.$store.dispatch('reconnectSocket')
      }
    },
    /**
     * Check WebSocket connection status and reconnect if needed
     */
    checkWebSocketConnection() {
      if (this.user && !this.online) {
        this.ensureWebSocketConnection()
      }
    },
    /**
     * Generate alert popup with message
     * @param {String} message 
     */
    alert(message) {
      alert(message)
    },
    isOwner(item) {
      if (!this.isObject(item) || !('owner' in item)) return false
      return this.user.id == item.owner.id
    },
    /**
     * Check if object is empty
     * @param {Mixed} item 
     * @returns {Boolean}
     */
    isEmpty(item) {
      if (item === undefined) return true
      if (item === null) return true
      if (this.isObject(item))
        return Object.entries(item).length === 0
      else if (Array.isArray(item))
        return item.length === 0
      return false
    },
    /**
     * Check if object is actually an object
     * @param {Object} obj 
     * @returns {Boolean}
     */
    isObject (input) {
      return (input instanceof Object && input !== null)
    },
    /**
     * Compares two arrays for equality
     * @param {Array} a 
     * @param {Array} b 
     */
    isEqual(a, b) {
      return a.length === b.length && a.every((value, index) => value === b[index])
    },
    /**
     * Check if route matches name
     * @param {String} name 
     * @returns {Boolean}
     */
    is_route(name) {
      return this.$route.name == name
    },
    /**
     * Check if route contains name
     * @param {String} segment 
     * @returns {Boolean}
     */
    route_contains(segment) {
      if (Array.isArray(segment)) {
        return segment.some(v => this.$route.path.includes(v))
      } else {
        return this.$route.path.split('/').includes(segment)
      }
    },
    /**
     * Creates an array of size with optional values
     * @param {Number} min Lower index
     * @param {Number} max Upper index
     * @param {Mixed} value Fill content
     * @returns {Array}
     */
    create_array(min=1, max=100, value = null) {
      return Array(max - min + 1).fill().map(() => value || min++)
    },
    /**
     * Pluralizes input text using number
     * @param {String} text 
     * @param {Number} number 
     * @returns {String}
     */
    pluralize(text, number) {
      return pluralize(text, number)
    },
    /**
     * Capitlizes first letter of string
     * @param {String} text 
     * @returns {String}
     */
    capitalize(text) {
      text = String(text)
      return text.charAt(0).toUpperCase() + text.slice(1)
    },
    /**
     * Formats the number adds commas
     * @param {Number} value 
     * @returns {String}
     */
    numberFormat(value, round=false, decimal=0) {
      if (value == Infinity) return 'Infinity'
      return numeral(Math.round(value, decimal)).format(round ? '0,0.00a' : '0,0')
    },
    /**
     * Returns number rounded to specification
     * @param {Number} num 
     * @returns Number
     */
    roundNumber(num, units=2) {
      let factor = 10 ** units
      return Math.round((num + Number.EPSILON) * factor) / factor
    },
    /**
     * Formats the number adds commas
     * @param {Number} value 
     * @returns {String}
     */
    byteFormat(value) {
      return numeral(value).format('0.0 b')
    },
    /**
     * Returns human readable date
     * @param {String} date 
     * @returns 
     */
    fromNow(date) {
      return moment(date).fromNow()
    },
    /**
     * Triggers snackbar
     * @param {String} message 
     * @param {String} color 
     */
    notify(message, color=null) {
      this.$store.commit('showSnackbar', { text: message, color: color })
    },
    /**
     * Calculate table rows that can fill the screen
     * @returns {Number} Requested rows of data
     */
    rows() {
      return Math.floor((window.innerHeight - 96 - 64 - 48 - 32) / 32)
    },
    /**
     * Commits refesh mutation
     */
    refresh() {
      this.$store.commit("refresh")
    },
    /**
     * JSON parses a string
     * @param {String} text
     * @returns {Object} 
     */
    json_parse(text) {
      return JSON.parse(text.split("'").join('"').split("None").join('null'))
    },
    /**
     * Deep clone an object/array (removes Vue reactivity)
     * @param {Mixed} obj
     * @returns {Mixed} 
     */
    clone(obj) {
      return structuredClone(obj)
    },
    /**
     * Creates a array from start date of defined length
     * @param {Date} start 
     * @param {Number} count 
     * @param {String} interval 
     * @returns {Array}
     */
    create_date_array(start, count = 12, interval = 'month') {
      return Array.from(moment.rangeFromInterval(interval, count - 1, start).by('month')).map(m => m.format('YYYY-MM-DD'))
    },
    /**
     * Merges (zips) two arrays 
     * Overrides portion of B that overlaps A
     * @param Array a 
     * @param Array b 
     * @returns {Array}
     */
    merge_arrays(a = [], b = []) {
      return a.concat(b.slice(a.length))
    },
    /**
     * Return running total array
     * @param {Array} input
     * @returns {Array}
     */
    cumulative(arr) {
      if (!Array.isArray(arr)) return 0
      return arr.slice(0, this.last_valid_index(arr)).map((s => a => s += a)(0))
    },
    /**
     * Return trimmed array based on last valid index
     * @param {Array} arr 
     * @returns Array
     */
    trim(arr) {
      return arr.slice(0, this.last_valid_index(arr))
    },
    /**
     * Calculate sum of array
     * @param {Array} arr Input array
     * @returns Number
     */
    sum(arr) {
      if (!Array.isArray(arr)) return 0
      return arr.reduce((a, b) => a + b, 0)
    },
    /**
     * Calculate mean of array
     * @param {Array} arr Input array
     * @returns Number
     */
    mean(arr) {
      if (!Array.isArray(arr)) return 0
      return this.sum(arr) / this.counta(arr)
    },
    /**
     * Calculate standard deviation of array
     * @param {Array} arr Input array
     * @returns Number
     */
    std(arr) {
      if (!Array.isArray(arr)) return 0
      if (this.isEmpty(arr)) return 0
      return Math.sqrt(arr.map(x => Math.pow(x - this.mean(arr), 2)).reduce((a, b) => a + b) / arr.length)
    },
    /**
     * Count non-null elements
     * @param {Array} arr Input array
     * @returns Number
     */
    counta(arr) {
      if (!Array.isArray(arr)) return 0
      return arr.filter(e => e !== null).length
    },
    /**
     * Returns index for last non-NA/null value.
     * @param {Array} arr Input array
     * @returns Number
     */
    last_valid_index(arr) {
      if (!Array.isArray(arr)) return 0
      // var index = arr.length
      // while (index-- && !arr[index])
      // return index + 1
      const last = [...arr].reverse().find(x => x != null)
      return arr.lastIndexOf(last) + 1
    },
    /**
     * Calculate quantiles of array
     * @param {Array} arr Input array
     * @param {Number} q Quantile
     * @returns Number
     */
    quantile(arr, q)  {
      if (!Array.isArray(arr)) return 0
      const sorted = arr.sort(function(a, b){return a - b})
      const pos = (sorted.length - 1) * q
      const base = Math.floor(pos)
      const rest = pos - base
      if (sorted[base + 1] !== undefined) {
          return Math.round(sorted[base] + rest * (sorted[base + 1] - sorted[base]))
      } else {
          return Math.round(sorted[base])
      }
    },
    flip_array(array) {
      return array.map((item,idx) => array[array.length-1-idx])
    },
    percentile(arr, reverse=false) {
      if (!Array.isArray(arr)) return arr
      const sorted = arr.slice().sort(function(a, b){return a - b})
      if (reverse) {
        sorted.reverse()
      }
      return arr.slice().map(e => percentRank(sorted, e))
    },
    /**
     * Filter an object by key>value pair
     * @param {Object} obj 
     * @param {String} key 
     * @param {String} value 
     * @returns Object
     */
    filterObject(obj, key, value) {
      if (!this.isObject(obj)) return obj
      let list = Object.keys(Object.fromEntries(Object.entries(obj).filter(([k, v]) => v[key] == value)))
      let out = []
      list.forEach(x => out.push({'text': x, 'value': x}))
      return out
    },
    /**
     * Filter object by keys from list
     * @param {Object} input 
     * @param {Array} arr 
     * @returns Object
     */
    filtered(obj, arr) {
      if (!this.isObject(obj)) return obj
      return Object.keys(obj).filter(key => arr.includes(key)).reduce((value, key) => { return {...value, [key]: obj[key] } }, {})
    },
    /**
     * Compute linear regression using least squares
     * @param {Array} x 
     * @param {Array} y 
     * @returns Array
     */
    linearRegression(x,y){
      var lr = {}
      var n = y.length
      var sum_x = 0
      var sum_y = 0
      var sum_xy = 0
      var sum_xx = 0
      var sum_yy = 0

      for (var i = 0; i < y.length; i++) {

          sum_x += x[i]
          sum_y += y[i]
          sum_xy += (x[i]*y[i])
          sum_xx += (x[i]*x[i])
          sum_yy += (y[i]*y[i])
      } 

      lr['sl'] = (n * sum_xy - sum_x * sum_y) / (n*sum_xx - sum_x * sum_x)
      lr['off'] = (sum_y - lr.sl * sum_x)/n
      lr['r2'] = Math.pow((n*sum_xy - sum_x*sum_y)/Math.sqrt((n*sum_xx-sum_x*sum_x)*(n*sum_yy-sum_y*sum_y)),2)

      return lr
    },
    /**
     * Finds and sets the value of path in object
     * 
     * @param {Object} obj
     * @param {String} path 
     * @param {Mixed} value 
     */
    setPath(obj, path, value) {
      path.split('.').reduce((o,p,i) => o[p] = path.split('.').length === ++i ? value : o[p] || {}, obj)
    },
    /**
     * Moves items within array using index
     * @param {Array} arr 
     * @param {Number} old_index 
     * @param {Number} new_index 
     * @returns 
     */
    array_move(arr, old_index, new_index) {
      if (new_index >= arr.length) {
          var k = new_index - arr.length + 1
          while (k--) {
              arr.push(undefined)
          }
      }
      arr.splice(new_index, 0, arr.splice(old_index, 1)[0])
      return arr // for testing
    },
    onIntersect(entries, observer) {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          let target = entry.target.id
          this.$refs.contents.selected = target
        }
      }
    },
    camelize(input) {
      return camelCase(input)
    },
    /**
     * Zips two arrays into an object that uses first array as keys and second as values
     * @param {Array} a 
     * @param {Array} b 
     * @returns Object
     */
    zip(a, b) {
      return Object.fromEntries(a.map((k, i) => [k, b[i]]))
    },
    /**
     * Calculate time difference in seconds
     * @param {DateTime} a 
     * @param {DateTime} b 
     * @returns String
     */
    date_diff(a, b) {
      a = moment(a)
      b = moment(b)
      return b.from(a, 'seconds')
    },
    sort_object(obj) {
      return Object.keys(obj).sort().reduce((r, k) => (r[k] = obj[k], r), {})
    },
    array_diff(a, b) {
      return a.map((x, i) => {
        return x - b[i]
      })
    },
    median(numbers) {
      const sorted = Array.from(numbers).sort((a, b) => a - b)
      const middle = Math.floor(sorted.length / 2)
  
      if (sorted.length % 2 === 0) {
          return (sorted[middle - 1] + sorted[middle]) / 2
      }
  
      return sorted[middle]
    },
    /**
     * Calculate MAPE between arrays
     * @param {Array} a 
     * @param {Array} b 
     * @returns Number
     */
    mape(a, b, method='mean') {
      let n = a.length
      let ape = []
      for (let i = 0; i < n; ++i) {
        ape[i] = Math.abs((a[i] - b[i]) / a[i])
      }
      if (method == 'mean') return this.sum(ape)/n
      else return this.median(ape)
    },
  }
}